import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  adminDecideLiveCancelRequest,
  adminListLiveCancelRequests,
  AdminLiveCancelRequest,
} from '../../api/payment/brand-live.api';
import { hoursLabel, validateReason } from '../../utils/brand-live';
import AdminLiveDecisionModal from './admin-live-decision-modal';
import { AdminOrdererLabel } from '../brand-deal/orderer-badge';

// Tab "Yêu cầu dừng livestream": creator xin dừng vì doanh nghiệp có phốt. Cũ nhất lên đầu, tiền đang giữ đến khi admin quyết.

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;
const fmtVnd = (n: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;
const fmtTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('vi-VN') : '');

interface Modal {
  req: AdminLiveCancelRequest;
  approve: boolean;
  reason: string;
  totp: string;
}

const AdminLiveCancelRequests: React.FC<{ onCount?: (n: number) => void }> = ({ onCount }) => {
  const [items, setItems] = useState<AdminLiveCancelRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<Modal | null>(null);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);

  const load = useCallback(async () => {
    try {
      const list = await adminListLiveCancelRequests('Pending');
      setItems(list);
      onCount?.(list.length);
    } catch (e) {
      toast.error(errorMessage(e, 'Không tải được danh sách.'));
    } finally {
      setLoading(false);
    }
  }, [onCount]);

  useEffect(() => {
    load();
  }, [load]);

  const confirm = async () => {
    if (!modal || lock.current) return;
    const invalid = validateReason(modal.reason);
    if (invalid) return toast.error(invalid);
    if (modal.totp.length !== 6) return toast.error('Cần mã Authenticator 6 số.');
    lock.current = true;
    setBusy(true);
    try {
      await adminDecideLiveCancelRequest(modal.req.id, modal.approve, modal.totp, modal.reason.trim());
      toast.success(modal.approve ? 'Đã đồng ý. Creator được trả mọi giây hợp lệ.' : 'Đã không đồng ý yêu cầu dừng.');
      setModal(null);
      await load();
    } catch (e) {
      toast.error(errorMessage(e, 'Mã Authenticator không hợp lệ hoặc xử lý thất bại.'));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  if (loading) return <p className="text-gray-500">Đang tải…</p>;
  if (items.length === 0) return <p className="text-gray-500">Không có yêu cầu dừng livestream nào đang chờ.</p>;

  return (
    <div className="space-y-3">
      <p className="text-sm text-amber-600 dark:text-amber-400">
        Có {items.length} yêu cầu đang chờ. Tiền của các deal này vẫn đang giữ cho đến khi bạn quyết.
      </p>
      {items.map((r) => (
        <div key={r.id} className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-boxdark">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 text-sm">
              <div className="font-semibold dark:text-white">
                Creator {r.creatorName} <span className="font-normal text-gray-500">· <AdminOrdererLabel name={r.businessName} kind={r.ordererKind} ownerIdentityName={r.ownerIdentityName} /></span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Gửi lúc {fmtTime(r.createdAt)} · Giá {fmtVnd(r.priceVnd)} · Đã phát {hoursLabel(r.shownSeconds)} /{' '}
                {hoursLabel(r.purchasedSeconds)} · Đã trả {fmtVnd(r.paidGrossVnd)}
              </div>
              <div className="mt-1 text-gray-700 dark:text-gray-200">
                Lý do: {r.reasonCode === 'BrandScandal' ? 'Doanh nghiệp có phốt hoặc vi phạm' : r.reasonCode}
              </div>
              <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-200">{r.reasonText}</div>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                onClick={() => setModal({ req: r, approve: true, reason: '', totp: '' })}
                className="rounded bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-700"
              >
                Đồng ý
              </button>
              <button
                onClick={() => setModal({ req: r, approve: false, reason: '', totp: '' })}
                className="rounded bg-rose-600 px-3 py-1.5 text-sm text-white hover:bg-rose-700"
              >
                Không đồng ý
              </button>
            </div>
          </div>
        </div>
      ))}
      {modal && (
        <AdminLiveDecisionModal
          title={modal.approve ? 'Đồng ý yêu cầu dừng' : 'Không đồng ý yêu cầu dừng'}
          description={
            modal.approve
              ? 'Creator được trả mọi giây hợp lệ của deal này.'
              : 'Creator không nhận thêm tiền, phần đang giữ hoàn cho doanh nghiệp.'
          }
          reason={modal.reason}
          totp={modal.totp}
          busy={busy}
          onReason={(v) => setModal((p) => (p ? { ...p, reason: v } : p))}
          onTotp={(v) => setModal((p) => (p ? { ...p, totp: v } : p))}
          onClose={() => setModal(null)}
          onConfirm={confirm}
        />
      )}
    </div>
  );
};

export default AdminLiveCancelRequests;
